import { Shield, Clock, MessageSquareReply, FileCheck2, FileSpreadsheet, Lock, Download } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const VAULT_ITEMS = [
  {
    icon: Shield,
    type: "PDF · 1 Seite",
    title: "MDK-Soforthilfe-Protokoll",
    subtitle: "Die ersten 30 Minuten der Prüfung",
    desc: "Glasklare Checkliste für den Tag, an dem der Medizinische Dienst unangemeldet vor der Tür steht. Wer wird angerufen? Welche Fragen darf man vertagen? Souverän & rechtssicher.",
    accent: "bg-terra/15 text-terra",
  },
  {
    icon: Clock,
    type: "PDF · 4 Seiten · Vorlage",
    title: "Der Erschöpfungs-Dienstplan",
    subtitle: "Notfallkonzept bei akutem Personalausfall",
    desc: "Zwei Notfallstufen (Gelb/Rot), die rechtssichere Maßnahmen-Kaskade nach ArbZG, fertige MAV-Mail, Aushang-Formulierung & Notfall-Protokoll zum Ausfüllen. Versorgung sichern – ohne Gesetzeskonflikt.",
    accent: "bg-brand-rose/30 text-brand-darkred",
  },
  {
    icon: MessageSquareReply,
    type: "PDF · 5 Seiten · 4 Fälle",
    title: "Angehörigen-Beschwerde",
    subtitle: "5-Minuten-Leitfaden mit Textbausteinen",
    desc: "Vier fertige Antwort-Mails für die brisantesten Fälle: mangelnde Pflege, verlorene Kleidung, Essens-Beschwerden, aggressive Mails. Plus 4-Schritte-Prinzip, Do's & Don'ts, Eskalations-Stufen. Drei Wörter anpassen – fertig.",
    accent: "bg-powder/60 text-powder-dark",
  },
  {
    icon: FileCheck2,
    type: "PDF · 4 Seiten · Spickzettel",
    title: "Entbürokratisierungs-Checkliste",
    subtitle: "Der SIS-Filter: MDK-sicher & schlank",
    desc: "Für jedes SIS-Themenfeld (A1–A6) + Pflegebericht: Was kann weg, was muss rein. Plus Quick-Filter aus 4 Fragen. Bis zu 30–60 Minuten Schreibarbeit pro Schicht gespart – die Zeit geht zurück zum Bewohner.",
    accent: "bg-terra/15 text-terra",
  },
  {
    icon: FileSpreadsheet,
    type: "XLSX + PDF · 4 Sheets",
    title: "Pflegesatz-Verhandlungs-Matrix",
    subtitle: "Der Pflegegrad-Mix als Verhandlungs-Hebel",
    desc: "Echte Excel-Vorlage mit Daten-Validierungen, Formeln & Auswertungs-Panel + branded Leitfaden mit 3 Verhandlungs-Argumenten und 4-Wochen-Vorbereitungsplan. Im Schnitt 3–7 % höhere Pflegesätze – refinanziert den Campus x-fach.",
    accent: "bg-brand-rose/30 text-brand-darkred",
  },
];

export default function HeldenSchatzkiste() {
  return (
    <section id="schatzkiste" className="bg-peach-50 py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                Helden-Schatzkiste · Exklusiv im Campus
              </span>
            </div>
            <h2 className="font-display uppercase text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 tracking-tight leading-[0.92]">
              Deine digitale <span className="text-terra">Zeitmaschine.</span>
            </h2>
            <p className="mt-6 text-sand-700 text-[15px] sm:text-base leading-[1.7] max-w-2xl">
              5 Knaller-Dokumente, die deinen Pflegealltag rechtssicherer und entspannter machen.
              Sofort einsatzbereit nach Freischaltung – wachsende Bibliothek, monatlich erweitert.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {VAULT_ITEMS.map((item, i) => (
            <Reveal key={item.title} delay={i * 90}>
              <article className="group relative h-full flex flex-col rounded-3xl bg-white border border-cream-300 shadow-soft hover:shadow-hover hover:-translate-y-1 transition-all duration-500 p-7 md:p-8">
                {/* Locked overlay – signaling exclusive */}
                <div className="absolute top-6 right-6 inline-flex items-center gap-1.5 rounded-full bg-cream-100 text-clay-dark px-3 py-1 text-[10px] font-bold tracking-[0.18em] uppercase">
                  <Lock className="h-3 w-3" strokeWidth={2.4} />
                  Campus-only
                </div>

                <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${item.accent} mb-5 transition-transform group-hover:scale-110`}>
                  <item.icon className="h-5 w-5" strokeWidth={1.6} />
                </span>

                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-clay-dark mb-2">
                  {item.type}
                </div>
                <h3 className="font-display text-xl md:text-[1.4rem] font-semibold text-sand-800 leading-tight">
                  {item.title}
                </h3>
                <p className="mt-1.5 font-display italic text-sand-600 text-[15px] leading-snug">
                  {item.subtitle}
                </p>

                <p className="mt-4 text-sand-700 text-[13.5px] leading-relaxed flex-1">
                  {item.desc}
                </p>

                <div className="mt-6 pt-4 border-t border-cream-200 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-terra">
                  <Download className="h-3.5 w-3.5" strokeWidth={2.4} />
                  Sofortiger Download nach Freischaltung
                </div>
              </article>
            </Reveal>
          ))}

          {/* "Mehr kommt"-Karte */}
          <Reveal delay={450}>
            <div className="h-full flex flex-col items-center justify-center text-center rounded-3xl border-2 border-dashed border-clay/40 p-8 md:p-9 bg-cream-50/40">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-cream-100 text-clay-dark mb-4">
                <span className="font-display text-2xl font-bold">+</span>
              </div>
              <h3 className="font-display text-xl text-sand-800 font-semibold">
                Monatlich neue Vorlagen.
              </h3>
              <p className="mt-3 text-sand-600 text-[13px] leading-relaxed">
                Auf Wunsch der Community: wir liefern monatlich 1–2 neue Mustervorlagen,
                Checklisten oder Argumentations­hilfen. Du brauchst etwas Spezifisches?
                Sag Bescheid – wir bauen es.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
